import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Info } from "lucide-react";
import { toast } from "sonner";

const countries = [
  "Italy", "Germany", "France", "Spain", "United Kingdom", "United States",
  "Switzerland", "Austria", "Netherlands", "Belgium", "Portugal", "Greece",
];

const PAYMENT_METHODS = [
  { id: "bank_transfer", label: "Bank Transfer" },
  { id: "paypal", label: "PayPal" },
  { id: "revolut", label: "Revolut" },
];

function HelpText({ text }) {
  return (
    <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
      <Info className="w-3 h-3 shrink-0" /> {text}
    </p>
  );
}

function PaymentMethodTab() {
  const { t } = useTranslation();
  const [method, setMethod] = useState("");
  const [details, setDetails] = useState({});
  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        const { data } = await api.get("/payment");
        setMethod(data.method || "");
        setDetails(data.details || {});
      } catch { /* empty */ }
    };
    fetchPayment();
  }, []);

  const handleMethodSelect = (m) => {
    setMethod(m);
    setDetails({});
    setEmailError("");
  };

  const updateDetail = (key, value) => {
    setDetails((prev) => ({ ...prev, [key]: value }));
    if (key === "paypal_email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(value && !emailRegex.test(value) ? "Please enter a valid email address" : "");
    }
  };

  const handleSave = async () => {
    if (method === "paypal" && emailError) return;
    setSaving(true);
    try {
      await api.put("/payment", { method, details });
      toast.success("Payment method saved");
    } catch {
      toast.error("Failed to save payment method");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 space-y-6" data-testid="payment-method-tab">
      <div>
        <Label className="text-sm font-medium">Select your payment method</Label>
        <p className="text-xs text-slate-400 mt-1">Choose one method to receive your payouts. Only one can be active at a time.</p>
        <Select value={method} onValueChange={handleMethodSelect}>
          <SelectTrigger className="mt-3 max-w-xs" data-testid="payment-method-select">
            <SelectValue placeholder="Select payment method" />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_METHODS.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {method === "bank_transfer" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Country</Label>
            <Select value={details.country || ""} onValueChange={(v) => updateDetail("country", v)}>
              <SelectTrigger data-testid="payment-country"><SelectValue placeholder="Select country" /></SelectTrigger>
              <SelectContent>
                {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <HelpText text="Country where your bank account is held" />
          </div>
          <div>
            <Label>IBAN</Label>
            <Input value={details.iban || ""} onChange={(e) => updateDetail("iban", e.target.value)} data-testid="payment-iban" placeholder="e.g. IT60X0542811101000000123456" />
            <HelpText text="International Bank Account Number" />
          </div>
          <div>
            <Label>SWIFT/BIC</Label>
            <Input value={details.swift || ""} onChange={(e) => updateDetail("swift", e.target.value)} data-testid="payment-swift" placeholder="e.g. BPMOIT22XXX" />
            <HelpText text="Bank identification code for international transfers" />
          </div>
          <div>
            <Label>Account Title</Label>
            <Input value={details.account_title || ""} onChange={(e) => updateDetail("account_title", e.target.value)} data-testid="payment-account_title" placeholder="e.g. John Smith" />
            <HelpText text="Full name on the bank account" />
          </div>
          <div>
            <Label>Bank Name</Label>
            <Input value={details.bank_name || ""} onChange={(e) => updateDetail("bank_name", e.target.value)} data-testid="payment-bank_name" placeholder="e.g. Banca Popolare di Milano" />
            <HelpText text="Name of your bank" />
          </div>
          <div>
            <Label>Bank City</Label>
            <Input value={details.bank_city || ""} onChange={(e) => updateDetail("bank_city", e.target.value)} data-testid="payment-bank_city" placeholder="e.g. Milan" />
            <HelpText text="City where the bank branch is located" />
          </div>
          <div>
            <Label>Bank Address</Label>
            <Input value={details.bank_address || ""} onChange={(e) => updateDetail("bank_address", e.target.value)} data-testid="payment-bank_address" placeholder="e.g. Via Roma 1" />
            <HelpText text="Full address of the bank branch" />
          </div>
          <div>
            <Label>Bank ZIP</Label>
            <Input value={details.bank_zip || ""} onChange={(e) => updateDetail("bank_zip", e.target.value)} data-testid="payment-bank_zip" placeholder="e.g. 20121" />
            <HelpText text="Postal code of the bank branch" />
          </div>
        </div>
      )}

      {method === "paypal" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>PayPal Email</Label>
            <Input
              type="email"
              value={details.paypal_email || ""}
              onChange={(e) => updateDetail("paypal_email", e.target.value)}
              data-testid="payment-paypal-email"
              placeholder="e.g. yourname@email.com"
            />
            {emailError ? (
              <p className="text-xs text-red-500 mt-1" data-testid="paypal-email-error">{emailError}</p>
            ) : (
              <HelpText text="The email address linked to your PayPal account" />
            )}
          </div>
          <div>
            <Label>Account Name</Label>
            <Input value={details.account_name || ""} onChange={(e) => updateDetail("account_name", e.target.value)} data-testid="payment-account-name" placeholder="e.g. John Smith" />
            <HelpText text="Full name registered on your PayPal account" />
          </div>
        </div>
      )}

      {method === "revolut" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Revolut Tag</Label>
            <Input value={details.revolut_tag || ""} onChange={(e) => updateDetail("revolut_tag", e.target.value)} data-testid="payment-revolut-tag" placeholder="e.g. @johnsmith" />
            <HelpText text="Your unique Revolut username/tag" />
          </div>
          <div>
            <Label>Full Name</Label>
            <Input value={details.full_name || ""} onChange={(e) => updateDetail("full_name", e.target.value)} data-testid="payment-full-name" placeholder="e.g. John Smith" />
            <HelpText text="Full name on your Revolut account" />
          </div>
        </div>
      )}

      {method && (
        <Button
          onClick={handleSave}
          disabled={saving || (method === "paypal" && !!emailError)}
          className="bg-[#1A1B2E] hover:bg-[#2D2E45]"
          data-testid="save-payment-btn"
        >
          {saving ? "Saving..." : "Save Payment Method"}
        </Button>
      )}
    </div>
  );
}

function PaymentsTab() {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [financeData, setFinanceData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFinances = async () => {
      try {
        const { data } = await api.get("/finances");
        setRecords(data.records || []);
        setFinanceData(data || {});
      } catch {
        toast.error("Failed to load payment records");
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFinances();
  }, []);

  const getStatusStyle = (status) => {
    if (status === "Processed") return "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20";
    return "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1A1B2E] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="payments-history-tab">
      {/* Summary Cards - removed Payment Via */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Total Earnings</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{financeData.currency} {financeData.total_earnings}</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Transactions</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{records.length}</p>
        </div>
      </div>

      {/* Finance Table - removed Type and Receipt columns */}
      <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-b border-slate-200">
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</TableHead>
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">City</TableHead>
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tour Date</TableHead>
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</TableHead>
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Payment Date</TableHead>
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((r, i) => (
                <TableRow key={r.id} className="table-row-hover" data-testid={`finance-row-${i}`}>
                  <TableCell className="px-4 py-4 text-sm text-slate-600">{i + 1}</TableCell>
                  <TableCell className="px-4 py-4 text-sm font-medium text-slate-900">{r.tour_city}</TableCell>
                  <TableCell className="px-4 py-4 text-sm text-slate-600">{r.tour_date}</TableCell>
                  <TableCell className="px-4 py-4 text-sm font-semibold text-slate-900">{r.currency} {r.amount}</TableCell>
                  <TableCell className="px-4 py-4 text-sm text-slate-600">{r.payment_date}</TableCell>
                  <TableCell className="px-4 py-4">
                    <span className={`status-badge ${getStatusStyle(r.status)}`}>{r.status}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export default function FinancePage() {
  const { t } = useTranslation();

  return (
    <div className="animate-slideUp space-y-6" data-testid="finance-page">
      <p className="text-sm text-slate-500">Manage your payment method and view processed payments</p>
      <Tabs defaultValue="payment" data-testid="finance-tabs">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl">
          <TabsTrigger value="payment" data-testid="tab-payment-method" className="rounded-lg data-[state=active]:bg-[#1A1B2E] data-[state=active]:text-white">
            Payment Method
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments-history" className="rounded-lg data-[state=active]:bg-[#1A1B2E] data-[state=active]:text-white">
            Payments
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payment" className="mt-6">
          <PaymentMethodTab />
        </TabsContent>
        <TabsContent value="payments" className="mt-6">
          <PaymentsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
